import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./PromptManager.css";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext";
import MasterTable from "../../components/MasterTable/MasterTable";
import SearchBar from "../../components/SearchBar/SearchBar";

// ─── Assets ─────────────────────────────────────────────────────────────────
import promptIcon from "../../assets/images/prompt-manager-icon.svg";
// eslint-disable-next-line import/no-unresolved
import promptActive from "../../assets/images/active-prompt-manager.svg";
import promptHover from "../../assets/images/hover-prompt-manager.svg";
import filterIcon from "../../assets/images/filter-icon.svg";
import editIcon from "../../assets/images/edit-icon.svg";

// ─── Mock Data ───────────────────────────────────────────────────────────────

const MOCK_COUNTRIES = [
    { id: "in", code: "IN", name: "India", prompts: 142, onboarded: "12 Feb'26", status: "Active" },
    { id: "us", code: "US", name: "United States", prompts: 87, onboarded: "03 Jan'26", status: "Active" },
    { id: "br", code: "BR", name: "Brazil", prompts: 65, onboarded: "19 Mar'26", status: "In Progress" },
    { id: "de", code: "DE", name: "Germany", prompts: 110, onboarded: "07 Feb'26", status: "Active" },
    { id: "au", code: "AU", name: "Australia", prompts: 54, onboarded: "22 Apr'26", status: "In Progress" },
    { id: "za", code: "ZA", name: "South Africa", prompts: 39, onboarded: "15 May'26", status: "Active" },
];

const generatePrompts = (country) => {
    const baseProps = [
        "Active Ingredient",
        "Formulation Type",
        "Regulatory Objective",
        "Label Claim",
        "Safety Profile",
        "Efficacy Data",
        "Environmental Impact",
        "Toxicology Summary",
        "Storage Conditions",
        "Application Method",
    ];

    return Array.from({ length: 12 }, (_, i) => ({
        id: `${country.id}-prompt-${i + 1}`,
        property: baseProps[i % baseProps.length],
        question: `Lorem ipsum dolor sit amet, sectetur adipiscing elit, sed do eiusmod tem${i % 3 === 0 ? "_jlhdhs_extended_property" : ""}...`,
        instruction: "Lorem ipsum dolor sit amet, sectetur adipiscing elit, sed do eiusmod tem...",
        regOneObject: i % 2 === 0 ? "Document" : "Object",
        source: i % 3 === 0 ? "External" : "Internal",
        technicalFormulation: i % 2 === 0 ? "Formulation" : "Technical",
        lastModified: `2024-06-${String(10 + (i % 20)).padStart(2, "0")}`,
        classification: `Final R${i + 1}`,
    }));
};

// ─── Column Definition ───────────────────────────────────────────────────────

const PROMPT_COLUMNS = [
    {
        label: "Property",
        accessor: "property",
        width: 170,
        minWidth: 170,
        truncate: true,
        maxLen: 20,
    },
    {
        label: "Question",
        accessor: "question",
        width: 230,
        minWidth: 200,
        truncate: true,
        maxLen: 35,
    },
    {
        label: "Instruction",
        accessor: "instruction",
        width: 230,
        minWidth: 200,
        truncate: true,
        maxLen: 35,
    },
    {
        label: "RegOne Object",
        accessor: "regOneObject",
        width: 150,
        minWidth: 130,
        type: "badge",
        colorMap: {
            Document: "blue",
            Object: "purple",
        },
    },
    {
        label: "Source",
        accessor: "source",
        width: 130,
        minWidth: 110,
        type: "badge",
        colorMap: {
            Internal: "green",
            External: "orange",
        },
    },
    {
        label: "Technical/Formulation",
        accessor: "technicalFormulation",
        width: 190,
        minWidth: 170,
        type: "badge",
        colorMap: {
            Formulation: "purple",
            Technical: "red",
        },
    },
    {
        label: "Last Modified",
        accessor: "lastModified",
        width: 150,
        minWidth: 130,
        truncate: false,
    },
    {
        label: "Classification",
        accessor: "classification",
        width: 150,
        minWidth: 130,
        truncate: true,
        maxLen: 12,
    },
];

// ─── CountryCard ─────────────────────────────────────────────────────────────

const CountryCard = ({ country, onClick }) => {
    const isActive = country.status === "Active";

    return (
        <div className="pm-country-card" id={`country-card-${country.id}`}>
            <div className="pm-country-card-header">
                <div className="pm-country-avatar">
                    <span className="pm-country-avatar-text">{country.code}</span>
                </div>
                <div className="pm-country-info">
                    <span className="pm-country-name">{country.name}</span>
                    <span className="pm-country-meta">
                        {country.prompts} Prompts &middot; Onboarded {country.onboarded}
                    </span>
                </div>
                <span className={`pm-status-badge pm-status-badge--${isActive ? "active" : "progress"}`}>
                    {country.status}
                </span>
            </div>
            <div className="pm-country-card-footer">
                <button
                    id={`edit-prompts-${country.id}`}
                    className="pm-edit-btn"
                    onClick={() => onClick(country)}
                    type="button"
                >
                    <img src={editIcon} alt="" className="pm-btn-icon" />
                    Edit Prompts
                </button>
            </div>
        </div>
    );
};

// ─── PromptManager ───────────────────────────────────────────────────────────

const PromptManager = () => {
    const { setBreadcrumbs, resetBreadcrumbs } = useBreadcrumb();

    const [view, setView] = useState("list"); // "list" | "table"
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [promptSearch, setPromptSearch] = useState("");
    const [promptData, setPromptData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [activeFilter, setActiveFilter] = useState("all");
    const filterRef = React.useRef(null);

    // Close filter dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterOpen(false);
            }
        };

        if (filterOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [filterOpen]);

    // Pre-filter prompt rows by active filter and search query
    const filteredPrompts = useMemo(() => {
        let list = promptData;

        if (activeFilter && activeFilter !== "all") {
            const f = activeFilter.toLowerCase();
            list = list.filter((row) =>
                (row.regOneObject && row.regOneObject.toLowerCase() === f) ||
                (row.source && row.source.toLowerCase() === f) ||
                (row.technicalFormulation && row.technicalFormulation.toLowerCase() === f)
            );
        }

        if (promptSearch.trim()) {
            const q = promptSearch.toLowerCase();
            list = list.filter((row) =>
                PROMPT_COLUMNS.some((col) => {
                    const val = col.accessor ? row[col.accessor] : null;
                    return val != null && String(val).toLowerCase().includes(q);
                })
            );
        }

        return list;
    }, [promptData, promptSearch, activeFilter]);

    // ─── Breadcrumbs ────────────────────────────────────────────────────────

    const goBackToList = useCallback(() => {
        setView("list");
        setSelectedCountry(null);
        setSearchQuery("");
        resetBreadcrumbs();
    }, [resetBreadcrumbs]);

    useEffect(() => {
        if (view === "table" && selectedCountry) {
            setBreadcrumbs([
                {
                    id: "prompt-manager",
                    label: "Prompt Manager",
                    icon: { default: promptIcon, hover: promptHover },
                    onClick: goBackToList,
                },
                {
                    id: "country",
                    label: selectedCountry.name,
                },
            ]);
        } else {
            resetBreadcrumbs();
        }

        return () => {
            resetBreadcrumbs();
        };
    }, [view, selectedCountry, setBreadcrumbs, resetBreadcrumbs, goBackToList]);

    // ─── Handlers ───────────────────────────────────────────────────────────

    const handleCountryClick = (country) => {
        setSelectedCountry(country);
        setLoading(true);
        setView("table");
        setPromptSearch("");

        // Simulate API fetch
        setTimeout(() => {
            setPromptData(generatePrompts(country));
            setLoading(false);
        }, 800);
    };

    // ─── Filtered Countries ──────────────────────────────────────────────────

    const filteredCountries = MOCK_COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // ─── Render: Country List ────────────────────────────────────────────────

    if (view === "list") {
        return (
            <div className="pm-root">
                {/* Search bar */}
                <div className="pm-search-row">
                    <SearchBar
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Search countries..."
                        className="pm-country-searchbar"
                    />
                </div>

                {/* Country cards grid */}
                <div className="pm-cards-grid">
                    {filteredCountries.length === 0 ? (
                        <div className="pm-empty">No countries found.</div>
                    ) : (
                        filteredCountries.map((country) => (
                            <CountryCard
                                key={country.id}
                                country={country}
                                onClick={handleCountryClick}
                            />
                        ))
                    )}
                </div>
            </div>
        );
    }

    // ─── Render: Prompt Table ──────────────────────────────────────────

    return (
        <div className="pm-table-root">
            {/* Page-level toolbar: Filters button + Search bar — side by side */}
            <div className="pm-table-toolbar">
                <div className="pm-filters-wrapper" ref={filterRef}>
                    <button
                        id="pm-filters-btn"
                        className="pm-filters-btn"
                        onClick={() => setFilterOpen((p) => !p)}
                        type="button"
                    >
                        <img src={filterIcon} alt="" className="pm-btn-icon" />
                        Filters
                    </button>

                    {filterOpen && (
                        <div className="pm-filters-dropdown">
                            {[
                                { label: "All", value: "all" },
                                { label: "Formulation", value: "formulation" },
                                { label: "Technical", value: "technical" },
                                { label: "Internal", value: "internal" },
                                { label: "External", value: "external" },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`pm-filters-option ${activeFilter === opt.value ? "active" : ""}`}
                                    onClick={() => { setActiveFilter(opt.value); setFilterOpen(false); }}
                                    type="button"
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <SearchBar
                    value={promptSearch}
                    onChange={setPromptSearch}
                    placeholder="Search..."
                    className="pm-table-searchbar"
                />
            </div>

            <MasterTable
                columns={PROMPT_COLUMNS}
                data={filteredPrompts}
                loading={loading}
                emptyMessage="No prompts found for this country."
                rowKey="id"
            />
        </div>
    );
};

export default PromptManager;
